"use client";

import {
  ChevronDown,
  LogOutIcon,
  Settings,
  User,
  RefreshCw,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

import { useProfileDropdown } from "@/components/hooks/profileDropdown/useProfileDropDown.hook";

const ProfileDropdown = () => {
  const {
    isMounted,
    isLoading,
    error,
    changeRoute,
    handleRetry,
    isLoggingOut,
    handleLogout,
    // Computed values from hook
    fallbackInitials,
    displayName,
    displayEmail,
    userType,
    avatarImageUrl,
    // Dropdown state
    isOpen,
    setIsOpen,
  } = useProfileDropdown();

 

  if (!isMounted) {
    return null; // or a loading placeholder
  }

  return (
    <DropdownMenu 
      modal={false} 
      open={isOpen || isLoggingOut} 
      onOpenChange={(open) => {
        if (!isLoggingOut) {
          setIsOpen(open);
        }
      }}
    >
      <DropdownMenuTrigger 
        className="items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:rounded-full flex w-full cursor-pointer rounded-full"
        aria-label="Profile menu"
      >
        <Avatar className="h-[42px] !w-[42px]">
          <AvatarImage src={avatarImageUrl} />
          <AvatarFallback>
            {isLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <span className="text-sm font-medium">{fallbackInitials}</span>
            )}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        alignOffset={0}
        sideOffset={5}
        className="flex flex-col px-0 w-[230px]"
        align="end"
      >
        <DropdownMenuGroup>
          <div className="flex gap-4 items-start px-4 py-1">
            <Avatar className="w-[40px] h-[40px]">
              <AvatarImage src={avatarImageUrl} />
              <AvatarFallback className="">
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <span className="text-sm font-medium">
                    {fallbackInitials}
                  </span>
                )}
              </AvatarFallback>
            </Avatar>

            <div className="w-full min-w-0">
              {isLoading ? (
                <div className="space-y-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
                </div>
              ) : error ? (
                <div className="space-y-1">
                  <p className="text-sm font-bold text-red-600">
                    Error loading profile
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRetry}
                    className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700"
                  >
                    Click to retry
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm font-bold text-zinc-800 truncate">
                    {displayName}
                  </p>
                  <p className="text-xs break-words text-zinc-500 truncate">
                    {displayEmail}
                  </p>
                  {userType && (
                    <p className="text-xs text-zinc-400 mt-1">{userType}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />

        <DropdownMenuGroup className="px-1">
          <DropdownMenuItem
            className="flex font-medium leading-[22px] items-center gap-2 px-3 h-[35px] cursor-pointer"
            onClick={() =>
              changeRoute(
                userType === "Contractor"
                  ? "/contractor/settings"
                  : "/homeowner/settings"
              )
            }
          >
            <Settings size={16} /> Settings
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuGroup className="px-1">
          <DropdownMenuItem
            className="flex font-medium leading-[22px] items-center gap-2 px-3 h-[35px] cursor-pointer text-red-500 hover:!text-red-500 hover:bg-[#ff5d4d1f] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.preventDefault();
              handleLogout();
            }}
            disabled={isLoggingOut}
            onSelect={(e) => {
              // Always prevent dropdown from closing - we control it manually
              e.preventDefault();
            }}
          >
            {isLoggingOut ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Logging out...
              </>
            ) : (
              <>
                <LogOutIcon size={16} />
                Logout
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileDropdown;
